import { formatInTimeZone } from 'date-fns-tz';
import { useAtomValue } from 'jotai';
import { useEffect, useMemo, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { Form, FormControl, FormField, FormItem, useToaster } from '@crossnokaye/ux-common';

import { AlertDialogTitleHeader } from '@/components/molecules/alert-dialog-title-header';
import { CriticalAlertIcon } from '@/components/molecules/critical-alert-icon';
import { TooltipButton } from '@/components/molecules/tooltip-button';
import {
  CancelShutdownFormData,
  CancelShutdownFormSchema,
} from '@/components/shutdown-banner/schema';
import { useShutdownActionPermission } from '@/components/shutdown-banner/use-shutdown-action-permission';
import { createFacilityDate } from '@/helpers/datetime/facility-date';
import { formatUTCISO } from '@/helpers/datetime/utc-date';
import { useShutdownApp } from '@/hooks/query-hooks/scheduling';
import { usePatchScheduledAppSettings } from '@/hooks/query-hooks/settings';
import { useCurrentFacility } from '@/hooks/use-current-facility';
import { baseTimeZoneAtom, timezoneAtom } from '@/state/time.state';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogBody,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogFooterButtons,
  AlertDialogTrigger,
} from '@/ux-common/components/alert-dialog/alert-dialog';
import { Button } from '@/ux-common/components/button/button';
import { InputHelper } from '@/ux-common/components/input/input';
import { Textarea, TextareaCount, TextareaField } from '@/ux-common/components/textarea/textarea';

export const CancelShutdownAction: React.FC<{
  endTime: Date | undefined;
  startTime: Date | undefined;
}> = ({ endTime, startTime }) => {
  const { organizationId: orgId, agentId } = useCurrentFacility();
  const { data: shutdownApp } = useShutdownApp({
    orgId,
    agentId,
  });

  const { mutate: cancelShutdown } = usePatchScheduledAppSettings({
    orgId,
    agentId,
    appId: shutdownApp?.[0]?.appId,
  });

  const timeZone = useAtomValue(timezoneAtom);
  const baseTimeZone = useAtomValue(baseTimeZoneAtom);
  const { addErrorToast } = useToaster();

  // This guard, `canSave` and `submit` below are a second copy of
  // `shutdown-dialog.tsx`, which Extend still uses. Not extracted on purpose:
  // Extend is on the legacy surface only until the registry has a date-and-time
  // picker (UXD-190), and that file is deleted when it migrates.

  useEffect(() => {
    if (!baseTimeZone) {
      addErrorToast(
        'Timezone not found. We were unable to modify shutdowns. Please contact support.'
      );
    }
  }, [baseTimeZone, addErrorToast]);
  const cancelUntil = useMemo(() => {
    let cancelUntil = createFacilityDate();
    if (endTime) {
      cancelUntil = createFacilityDate(endTime);
    } else if (startTime) {
      cancelUntil = createFacilityDate(startTime);
    }

    // Set time to midnight of next day
    cancelUntil.setHours(24, 0, 0, 0);
    return cancelUntil;
  }, [endTime, startTime]);

  const { isDisabled, tooltipText } = useShutdownActionPermission();

  const [open, setOpen] = useState(false);

  const form = useForm<CancelShutdownFormData>({
    // Keeps the field controlled from the first render, so the dialog's
    // `form.reset()` clears the textarea itself and not just RHF state.
    defaultValues: { cancelShutdownReason: '' },
    resolver: zodResolver(CancelShutdownFormSchema),
    mode: 'onChange',
  });

  const onSubmit: SubmitHandler<CancelShutdownFormData> = async (data) => {
    await cancelShutdown([
      {
        name: 'shutdown_postpone_until',
        value: JSON.stringify(formatUTCISO(cancelUntil)),
        changeDescription: data.cancelShutdownReason,
      },
    ]);
  };

  // `baseTimeZoneAtom`, not `timezoneAtom`: the latter falls back to the local
  // zone and is always set, so gating on it would never block a save.
  const canSave =
    form.formState.isDirty &&
    !form.formState.isSubmitting &&
    form.formState.isValid &&
    !!baseTimeZone;

  const submit = async () => {
    await form.handleSubmit(onSubmit)();
    form.reset();
  };

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) form.reset();
      }}
    >
      <AlertDialogTrigger asChild>
        <TooltipButton
          data-component={'CancelShutdownAction'}
          className={'btn btn-inverse'}
          disabled={isDisabled}
          showTooltipWhenEnabled
          tooltipContent={tooltipText}
        >
          {'Cancel Shutdown'}
        </TooltipButton>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogTitleHeader
          icon={<CriticalAlertIcon />}
          title={'Are you sure you want to cancel the upcoming Shutdown?'}
        />
        <AlertDialogBody>
          <AlertDialogDescription asChild>
            <div className={'flex flex-col gap-4 text-body-base'}>
              <span>{`Shutdown will be cancelled until ${formatInTimeZone(cancelUntil, timeZone, "EEEE, MMMM do 'at' h:mm a zzz")}`}</span>
              <Form {...form}>
                <FormField
                  control={form.control}
                  name={'cancelShutdownReason'}
                  render={({ field, fieldState }) => (
                    <FormItem className={'text-left'}>
                      <Textarea className={'w-full'} state={fieldState.error ? 'error' : undefined}>
                        {/* A Radix `Slot`, so it has to wrap the control itself — around a
                      positioning div, the merged a11y props land on the div. */}
                        <FormControl>
                          <TextareaField
                            {...field}
                            // `FormControl` points this at a `FormDescription` the form never
                            // renders. `TextareaField` merges, so the counter stays announced.
                            aria-describedby={undefined}
                            controlClassName={'textarea-fixed text-foreground'}
                            data-testid={'cancel-reason'}
                            maxLength={500}
                            placeholder={'Briefly explain reasons for cancelling shutdown...'}
                          >
                            {/* Child, not sibling — its context is provided inside the field. */}
                            <TextareaCount />
                          </TextareaField>
                        </FormControl>
                        {/* Inside the group so it registers its own id with the control's
                      `aria-describedby`; a `FormMessage` sibling would announce nothing. */}
                        <InputHelper>{fieldState.error?.message}</InputHelper>
                      </Textarea>
                    </FormItem>
                  )}
                />
              </Form>
            </div>
          </AlertDialogDescription>
        </AlertDialogBody>
        <AlertDialogFooter>
          <AlertDialogFooterButtons>
            <AlertDialogCancel asChild>
              <Button className={'btn btn-tier3'}>{'No, Keep Shutdown'}</Button>
            </AlertDialogCancel>
            <AlertDialogAction asChild>
              <Button
                className={'btn btn-destructive'}
                disabled={!canSave}
                onClick={async (event) => {
                  // Radix closes on click unless the handler prevents it, and
                  // closing first tears the form down mid-submit.
                  event.preventDefault();
                  await submit();
                  setOpen(false);
                }}
              >
                {form.formState.isSubmitting ? 'Cancelling...' : 'Yes, Cancel Shutdown'}
              </Button>
            </AlertDialogAction>
          </AlertDialogFooterButtons>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
